import {
  internalGetReq,
  internalPostReq,
  internalPatchReq,
  internalKeepalivePatchReq,
  internalDeleteReq,
} from "./requestFunctions";

/**
 * Fail fast on the classic footgun: `params: { firmId }` instead of
 * `params: [firmId]`. The URL builder iterates params as an array, so an
 * object is silently dropped and the request hits the wrong route.
 */
const assertParamsIsArray = (options, method) => {
  if (options && options.params != null && !Array.isArray(options.params)) {
    throw new Error(
      `ApiRequest.${method}: options.params must be an ARRAY of URL path segments, e.g. params: [firmId]. Got ${typeof options.params}.`
    );
  }
};

/**
 * Authenticated API client for a specific route and auth type.
 * Wraps the internal request functions and builds URLs automatically.
 *
 * URL format: /api/{version}/{route}/{endpoint}_auth_{authType}/{...params}
 *
 * @param {string} route - API route segment (e.g. "users", "households") — use API_ROUTES constants
 * @param {string} authType - Auth type suffix (e.g. "advisor", "user") — use API_AUTH_TYPES constants
 */
class ApiRequest {
  constructor(route, authType) {
    this.internalGetReq = internalGetReq;
    this.internalPostReq = internalPostReq;
    this.internalPatchReq = internalPatchReq;
    this.internalKeepalivePatchReq = internalKeepalivePatchReq;
    this.internalDeleteReq = internalDeleteReq
    this.authType = authType;
    this.route = route;
  }

  /**
   * Sends a POST request.
   * @param {Object} options
   * @param {string} options.endpoint - Endpoint name appended to the route
   * @param {Object} [options.body={}] - Request body
   * @param {Array} [options.params=[]] - URL path segments appended after the endpoint
   * @param {Object} [options.queryParams={}] - Query string parameters
   * @param {number} [options.timeout] - Optional axios timeout in ms; defaults to global 30s.
   * @param {boolean} [options.retry=true] - Retry transient 5xx responses when enabled.
   * @param {AbortSignal} [options.signal] - Optional cancellation signal.
   * @returns {Promise<Object>} Response data
   */
  async post(options = { endpoint: "", body: {}, params: [], queryParams: {} }) {
    assertParamsIsArray(options, "post");
    return await this.internalPostReq(
      this.authType,
      this.route,
      options.endpoint,
      options.body,
      options.params,
      options.queryParams,
      "v1",
      options.timeout,
      options.retry !== false,
      options.signal
    );
  }

  /**
   * Returns an async function that sends a POST request.
   * Useful as a react-query mutationFn.
   * @param {Object} options - Same options as `post()`
   * @returns {Function} Async function that performs the POST
   */
  postFn(options = { endpoint: "",body:{}, params: [], queryParams: {} }) {
    assertParamsIsArray(options, "postFn");
    return async () =>
      await this.internalPostReq(
        this.authType,
        this.route,
        options.endpoint,
        options.body,
        options.params,
        options.queryParams
      );
  }

  /**
   * Sends a GET request.
   * @param {Object} options
   * @param {string} options.endpoint - Endpoint name
   * @param {Array} [options.params=[]] - URL path segments
   * @param {Object} [options.queryParams={}] - Query string parameters
   * @param {string} [options.version='v1'] - API version
   * @param {boolean} [options.fullRequest=false] - If true, returns the full axios response instead of just data
   * @param {AbortSignal} [options.signal] - Optional cancellation signal for this GET.
   * @param {number} [options.timeout] - Optional axios timeout in ms; defaults to global 30s.
   * @param {boolean} [options.retry=true] - Whether to retry transient GET errors.
   * @returns {Promise<Object>} Response data (or full response if fullRequest is true)
   */
  async get(options = { endpoint: "", params: [], queryParams: {}, fullRequest: false }) {
    assertParamsIsArray(options, "get");
    return await this.internalGetReq(
      this.authType,
      this.route,
      options.endpoint,
      options.params,
      options.version || 'v1',
      options.fullRequest || false,
      options.queryParams || {},
      { signal: options.signal, retry: options.retry !== false, timeout: options.timeout }
    );
  }

  /**
   * Returns an async function that sends a GET request.
   * Useful as a react-query queryFn.
   * @param {Object} options - Same options as `get()`
   * @returns {Function} Async function that performs the GET
   */
  getFn(options = { endpoint: "", params: [], queryParams: {} }) {
    assertParamsIsArray(options, "getFn");
    return async () =>
      await this.internalGetReq(
        this.authType,
        this.route,
        options.endpoint,
        options.params,
        options.version || 'v1',
        false,
        options.queryParams || {}
      );
  }

  /**
   * GET request to the `find_all` endpoint — returns all records for this route.
   * @returns {Promise<Object>}
   */
  async findAll() {
    const endpoint = "find_all";
    return await this.internalGetReq(this.authType, this.route, endpoint);
  }

  /**
   * GET request to the `find_all_my` endpoint — returns records belonging to the current user.
   * Uses the user_id from the access token as a search parameter.
   * @returns {Promise<Object>}
   */
  async findAllMy() {
    const endpoint = "find_all_my";
    return await this.internalGetReq(this.authType, this.route, endpoint);
  }

  /**
   * GET request to `find_many_where_{search}` — returns records matching a dynamic search field.
   * @param {string} search - Field name to search by (e.g. "household_id")
   * @param {Array} [params=[]] - URL path params passed after the endpoint
   * @returns {Promise<Object>}
   */
  async findManyWhere(search, params = []) {
    const endpoint = `find_many_where_${search}`;
    return await this.internalGetReq(
      this.authType,
      this.route,
      endpoint,
      params
    );
  }

  /**
   * GET request to the `find_unique` endpoint.
   * @param {Array} [params=[]] - URL path params
   * @returns {Promise<Function>} An async function that performs the request
   */
  async findUnique(params = []) {
    const endpoint = "find_unique";
    return async () =>
      await this.internalGetReq(this.authType, this.route, endpoint, params);
  }

  /**
   * POST request to the `create` endpoint.
   * @param {Object} body - Request body
   * @param {Array} [params=[]] - URL path params
   * @returns {Promise<Object>}
   */
  async create(body, params = []) {
    const endpoint = "create";
    return await this.internalPostReq(
      this.authType,
      this.route,
      endpoint,
      body,
      params
    );
  }

  /**
   * PATCH request to the `update` endpoint.
   * @param {Object} body - Fields to update
   * @param {Array} [params=[]] - URL path params
   * @returns {Promise<Object>}
   */
  async update(body, params = []) {
    const endpoint = "update";
    return await this.internalPatchReq(
      this.authType,
      this.route,
      endpoint,
      body,
      params
    );
  }

  /**
   * Sends a PATCH request to a custom endpoint.
   * @param {Object} options
   * @param {string} options.endpoint - Endpoint name
   * @param {Object} [options.body={}] - Request body
   * @param {Array} [options.params=[]] - URL path segments
   * @param {Object} [options.queryParams={}] - Query string parameters
   * @param {number} [options.timeout] - Optional axios timeout in ms; defaults to global 30s.
   * @param {boolean} [options.retry=true] - Retry transient 5xx responses when enabled.
   * @returns {Promise<Object>}
   */
  async patch(options = { endpoint: "", body: {}, params: [], queryParams: {} }) {
    assertParamsIsArray(options, "patch");
    return await this.internalPatchReq(
      this.authType,
      this.route,
      options.endpoint,
      options.body,
      options.params,
      options.queryParams,
      "v1",
      options.timeout,
      options.retry !== false
    );
  }

  /**
   * Sends a PATCH request that survives page unload (fetch keepalive).
   * Use for last-chance saves on pagehide/visibilitychange — normal requests
   * are aborted by the browser when the tab closes. No retries; the body must
   * stay under the browser's ~64KB keepalive budget or the promise rejects.
   * @param {Object} options - Same options as `patch()` (no timeout)
   * @returns {Promise<Object|null>}
   */
  async patchKeepalive(options = { endpoint: "", body: {}, params: [], queryParams: {} }) {
    assertParamsIsArray(options, "patchKeepalive");
    return await this.internalKeepalivePatchReq(
      this.authType,
      this.route,
      options.endpoint,
      options.body,
      options.params,
      options.queryParams,
      "v1"
    );
  }

  /**
   * Sends a DELETE request to a custom endpoint.
   * @param {Object} options
   * @param {string} options.endpoint - Endpoint name
   * @param {Array} [options.params=[]] - URL path segments
   * @param {Object} [options.queryParams={}] - Query string parameters
   * @param {Object} [options.body] - Optional request body
   * @returns {Promise<Object>}
   */
  async delete(options = { endpoint: "", params: [] , queryParams: {}, body: undefined }) {
    assertParamsIsArray(options, "delete");
    return await this.internalDeleteReq(
      this.authType,
      this.route,
      options.endpoint,
      options.params,
      options.queryParams,
      "v1",
      options.body
    );
  }

  /**
   * GET request to the `delete_unique` endpoint.
   * @param {Array} [params=[]] - URL path params identifying the record to delete
   * @returns {Promise<Object>}
   */
  async deleteUnique(params = []) {
    const endpoint = "delete_unique";

    return await this.internalGetReq(
      this.authType,
      this.route,
      endpoint,
      params
    );
  }
}

export default ApiRequest;
